import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, UserCheck, Edit, UserMinus, Search, Filter, Users, GraduationCap } from "lucide-react";

const EnrolledDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [dashboard, setDashboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [teacherName, setTeacherName] = useState("");
  
  // Navigation and filtering state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGradeFilter, setSelectedGradeFilter] = useState("all");
  const [filteredDashboard, setFilteredDashboard] = useState([]);
  
  // Withdraw student state
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawStudent, setWithdrawStudent] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    filterDashboard();
  }, [dashboard, searchTerm, selectedGradeFilter]);

  const filterDashboard = () => {
    let filtered = [...dashboard];

    // Filter by grade
    if (selectedGradeFilter !== "all") {
      filtered = filtered.filter(grade => grade.grade === selectedGradeFilter);
    }

    // Filter by search term (student name)
    if (searchTerm.trim()) {
      filtered = filtered.map(grade => ({
        ...grade,
        students: grade.students.filter(student => 
          `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(grade => grade.students.length > 0);
    }

    setFilteredDashboard(filtered);
  };

  const getTotalStudents = () => {
    return dashboard.reduce((sum, grade) => sum + grade.enrolled_count, 0);
  };

  const getGradesWithTeachers = () => {
    return dashboard.filter(grade => grade.homeroom_teacher).length;
  };

  const scrollToGrade = (gradeId) => {
    const element = document.getElementById(`grade-${gradeId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/enrolled-dashboard`);
      setDashboard(response.data);
    } catch (error) {
      toast.error("Failed to fetch enrolled dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSetTeacher = (grade, currentTeacher) => {
    setSelectedGrade(grade);
    setTeacherName(currentTeacher || "");
    setShowTeacherModal(true);
  };

  const handleSaveTeacher = async () => {
    if (!teacherName.trim()) {
      toast.error("Please enter teacher name");
      return;
    }

    try {
      await axios.put(`${API}/admin/set-homeroom-teacher`, {
        grade: selectedGrade,
        teacher_name: teacherName
      });
      toast.success("Homeroom teacher updated successfully");
      setShowTeacherModal(false);
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to update homeroom teacher");
    }
  };

  const handleWithdrawClick = (student, grade) => {
    setWithdrawStudent({ ...student, grade });
    setShowWithdrawDialog(true);
  };

  const handleConfirmWithdraw = async () => {
    if (!withdrawStudent) return;

    setWithdrawing(true);
    try {
      await axios.post(`${API}/admin/withdraw-student`, {
        application_code: withdrawStudent.application_code,
        grade: withdrawStudent.grade
      });
      toast.success(`${withdrawStudent.first_name} ${withdrawStudent.last_name} has been withdrawn successfully`);
      setShowWithdrawDialog(false);
      setWithdrawStudent(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to withdraw student");
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School" 
              className="h-12"
            />
            <div>
              <h1 className="text-xl font-bold text-[#0E366A] font-serif">Enrolled Students Dashboard</h1>
              <p className="text-sm text-[#0E366A] italic">2024-2025 Academic Year</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              data-testid="back-to-admin-btn"
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
              className="border-[#0E366A] text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              Back to Admin
            </Button>
            <Button 
              data-testid="logout-btn"
              variant="ghost" 
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-white">Loading enrolled students...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white border-2 border-[#F7B538]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Students</p>
                      <p className="text-3xl font-bold text-[#0E366A]">{getTotalStudents()}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#F7B538]/20 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#0E366A]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-green-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Grades</p>
                      <p className="text-3xl font-bold text-green-600">{dashboard.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-blue-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Teachers Assigned</p>
                      <p className="text-3xl font-bold text-blue-600">{getGradesWithTeachers()}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-purple-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Showing</p>
                      <p className="text-3xl font-bold text-purple-600">{filteredDashboard.length}</p>
                      <p className="text-xs text-gray-500">of {dashboard.length} grades</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Filter className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filter Bar */}
            <Card className="bg-white">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search */}
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-[#0E366A] font-semibold">Search Student</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search by student name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 border-[#0E366A]/20 focus:border-[#F7B538]"
                      />
                    </div>
                  </div>

                  {/* Grade Filter */}
                  <div className="space-y-2">
                    <Label className="text-[#0E366A] font-semibold">Filter by Grade</Label>
                    <Select value={selectedGradeFilter} onValueChange={setSelectedGradeFilter}>
                      <SelectTrigger className="border-[#0E366A]/20">
                        <SelectValue placeholder="All Grades" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Grades</SelectItem>
                        {dashboard.map(grade => (
                          <SelectItem key={grade.grade} value={grade.grade}>
                            {grade.grade} ({grade.enrolled_count} students)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Quick Jump Navigation */}
                {selectedGradeFilter === "all" && !searchTerm && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Label className="text-[#0E366A] font-semibold mb-2 block">Quick Jump to Grade</Label>
                    <div className="flex flex-wrap gap-2">
                      {dashboard.map(grade => (
                        <Button
                          key={grade.grade}
                          size="sm"
                          variant="outline"
                          onClick={() => scrollToGrade(grade.grade)}
                          className="border-[#F7B538] text-[#0E366A] hover:bg-[#F7B538]/20"
                        >
                          {grade.grade}
                          <Badge variant="secondary" className="ml-2 bg-[#F7B538]/20">
                            {grade.enrolled_count}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Results Info */}
                {searchTerm && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      {filteredDashboard.reduce((sum, g) => sum + g.students.length, 0)} student(s) found matching "{searchTerm}"
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Grades List */}
            {filteredDashboard.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No students found matching your search criteria</p>
                  <Button 
                    onClick={() => { setSearchTerm(""); setSelectedGradeFilter("all"); }}
                    className="mt-4 bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A]"
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredDashboard.map((gradeData, idx) => (
                <Card 
                  key={idx} 
                  id={`grade-${gradeData.grade}`}
                  data-testid={`grade-card-${gradeData.grade}`} 
                  className="bg-white scroll-mt-24"
                >
                  <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Badge className="text-2xl font-bold bg-[#0E366A] text-white px-4 py-2">
                          {gradeData.grade}
                        </Badge>
                        <span className="text-sm font-normal text-gray-600">
                          {gradeData.students.length} / {gradeData.capacity} Students
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {gradeData.homeroom_teacher && (
                          <div className="flex items-center bg-[#F7B538]/20 px-4 py-2 rounded-lg border border-[#F7B538]">
                            <UserCheck className="w-4 h-4 text-[#0E366A] mr-2" />
                            <span className="text-sm font-medium text-[#0E366A]">
                              {gradeData.homeroom_teacher}
                            </span>
                          </div>
                        )}
                        <Button
                          data-testid={`set-teacher-btn-${gradeData.grade}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetTeacher(gradeData.grade, gradeData.homeroom_teacher)}
                          className="border-[#0E366A] text-[#0E366A] hover:bg-[#F7B538]/20"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {gradeData.homeroom_teacher ? "Edit Teacher" : "Set Teacher"}
                        </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {gradeData.students.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No enrolled students in this grade</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F7B538]/10">
                          <TableHead className="w-12 text-[#0E366A] font-semibold">#</TableHead>
                          <TableHead className="text-[#0E366A] font-semibold">First Name</TableHead>
                          <TableHead className="text-[#0E366A] font-semibold">Last Name</TableHead>
                          <TableHead className="text-[#0E366A] font-semibold text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gradeData.students.map((student, studentIdx) => (
                          <TableRow key={studentIdx}>
                            <TableCell className="font-medium text-gray-500">{studentIdx + 1}</TableCell>
                            <TableCell className="font-medium text-[#0E366A]">{student.first_name}</TableCell>
                            <TableCell className="font-medium text-[#0E366A]">{student.last_name}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleWithdrawClick(student, gradeData.grade)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <UserMinus className="w-4 h-4 mr-1" />
                                Withdraw
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Set Teacher Modal */}
      <Dialog open={showTeacherModal} onOpenChange={setShowTeacherModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Set Homeroom Teacher - {selectedGrade}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              data-testid="teacher-name-input"
              placeholder="Enter teacher name"
              value={teacherName}
              onChange={(e) => setTeacherName(e.target.value)}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button 
              data-testid="cancel-teacher-btn"
              variant="outline" 
              onClick={() => setShowTeacherModal(false)}
            >
              Cancel
            </Button>
            <Button 
              data-testid="save-teacher-btn"
              onClick={handleSaveTeacher}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Student Confirmation Dialog */}
      <AlertDialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Withdraw Student</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawStudent && (
                <div className="space-y-2">
                  <p>Are you sure you want to withdraw the following student?</p>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-4">
                    <p className="font-semibold text-gray-900">
                      {withdrawStudent.first_name} {withdrawStudent.last_name}
                    </p>
                    <p className="text-sm text-gray-600">Grade: {withdrawStudent.grade}</p>
                    <p className="text-sm text-gray-600">Application Code: {withdrawStudent.application_code}</p>
                  </div>
                  <p className="text-red-600 font-medium">
                    ⚠️ This action will mark the student as withdrawn and increase the available spots for this grade by 1.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdrawing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmWithdraw}
              disabled={withdrawing}
              className="bg-red-600 hover:bg-red-700"
            >
              {withdrawing ? "Withdrawing..." : "Withdraw Student"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EnrolledDashboard;
